import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import useLanguage from "../hooks/useLanguage";
import {
  Typography,
  Grid,
  CircularProgress,
  Button,
  Box,
  LinearProgress,
  Paper,
  Alert,
  Modal,
  Backdrop,
  Fade,
  TextField,
  Snackbar,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import { useParams } from "react-router-dom";
import { getDeviceData } from "../api/devices";
import { sendNotification } from "../api/notifications";
import { getDeviceZoneGroups } from "../api/maps";
import { getDeviceCleaningConfigs } from "../api/cleaning";
import TopNavBar from "../components/TopNavBar";
import { useAuth } from "../context/AuthContext";
import useSSESubscription from "../hooks/useSSESubscription";
import controlTranslations from "../assets/variables/controlTranslations";
import {
  getButtonBackgroundColor,
  getStatusBarBackgroundColor,
  getStatusBarBorderColor,
  getStatusIcon,
  buttonStyles,
  modalStyles,
} from "../components/styles/deviceControlStyles";
import {
  sendDeviceControlCommand,
  sendMultiZoneControlCommand,
  submitCustomerFeedback,
} from "../api/controls";
import ControlCard from "../components/ControlCard";
import ControlSidebar from "../components/ControlSidebar";
import CustomerFeedbackModal from "../components/CustomerFeedbackModal";

import { useFullscreen } from "../hooks/useFullscreen";
import CleaningConfigSelector from "../components/CleaningConfigSelector";
import ControlCardSelector from "../components/ControlCardSelector";

const MULTI_CLEAN_ZONE_ID = "multi-clean-selector";
const MULTI_CLEAN_ZONE_NAME = "Multi Clean";
const NORMALIZED_MULTI_CLEAN_ZONE_NAME = MULTI_CLEAN_ZONE_NAME.toLowerCase();
const DEFAULT_CLEANING_LEVEL = 2; //TO DO - Define the default via the config page and add to the database table as a boolean for dynamic retrieval in future.
const LOW_RATING_THRESHOLD = 4;
const FEEDBACK_EXPERIENCE_OPTIONS = [
  {
    value: "Gum / sticky substance on floor",
    labelKey: "gumOnFloor",
    backgroundPosition: "0% 0%",
  },
  {
    value: "Robot could not reach or return to station",
    labelKey: "dockingIssue",
    backgroundPosition: "50% 0%",
  },
  {
    value: "Dirt left in corners / edges",
    labelKey: "dirtyEdges",
    backgroundPosition: "100% 0%",
  },
  {
    value: "Floor has visible marks / streaks",
    labelKey: "streaks",
    backgroundPosition: "0% 100%",
  },
  {
    value: "Door malfunction / doorway issue",
    labelKey: "doorIssue",
    backgroundPosition: "50% 100%",
  },
  {
    value: "Tablet or app malfunction / controls issue",
    labelKey: "controlsIssue",
    backgroundPosition: "100% 100%",
  },
];

export default function DeviceControlsPage() {
  const { user } = useAuth();
  const username = user?.username;
  const customerName = user?.customerName;
  const { rcDeviceId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // localStorage key for persisting currentzone per device
  const CURRENTZONE_STORAGE_KEY = `robot_currentzone_${rcDeviceId}`;

  // Add state for current device
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [deviceZoneData, setDeviceZoneData] = useState(null);
  const { enterFullscreen } = useFullscreen(false);
  const [deviceError, setDeviceError] = useState(null);

  // Cleaning Config data
  const [cleaningConfigPresent, setCleaningConfigPresent] = useState(false);

  // Language state - defaults to Danish
  const [language, setLanguage] = useLanguage();

  // Translation function
  const t = (key) => {
    const keys = key.split(".");
    let value = controlTranslations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  // Cleaning level state for passing to ControlSidebar and using in device actions
  const [cleaningLevel, setCleaningLevel] = useState(DEFAULT_CLEANING_LEVEL); // Default to "Normal" cleaning level

  // Toggle to show/hide CleaningConfigSelector. This is not the CleaningLevelSlider, but a different style of selector.
  const SHOW_CLEANING_CONFIG_SELECTOR = false;

  // Feedback modal configuration - TO DO: This should be configurable per location in future.
  const FEEDBACK_MODAL_SIZE = "lg"; // Can be 'sm', 'md', 'lg', or a custom object like { minWidth: 400, maxWidth: 600 }
  const FEEDBACK_SHOW_COMMENT = false;
  const FEEDBACK_STARTING_HOUR = 5; // 5am - feedback window opens at 05:00
  const FEEDBACK_EXPIRATION_HOUR = 8; // 8am - feedback window closes at 08:00

  // Language switcher function
  const toggleLanguage = () => {
    const newLanguage = language === "en" ? "da" : "en";
    setLanguage(newLanguage);
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper functions for currentzone persistence
  const saveCurrentZoneToStorage = useCallback(
    (zoneName) => {
      try {
        if (zoneName) {
          localStorage.setItem(CURRENTZONE_STORAGE_KEY, zoneName);
        } else {
          localStorage.removeItem(CURRENTZONE_STORAGE_KEY);
        }
      } catch (error) {
        console.error("Failed to save currentzone to localStorage:", error);
      }
    },
    [CURRENTZONE_STORAGE_KEY, rcDeviceId],
  );

  const getInitialCurrentZone = useCallback(() => {
    try {
      return localStorage.getItem(CURRENTZONE_STORAGE_KEY) || "";
    } catch (error) {
      console.error("Failed to load currentzone from localStorage:", error);
      return "";
    }
  }, [CURRENTZONE_STORAGE_KEY]);

  const initialCurrentZone = getInitialCurrentZone();

  // Mock robot status when app starts - will be synced with real device status once loaded
  const [robotStatus, setRobotStatus] = useState({
    state: "resting", // charging, cleaning, stopped, error, returning
    currentzone: initialCurrentZone,
    batteryLevel: 0,
    chargingProgress: 0,
  });

  // zones from real deviceZoneData with state management
  const [zones, setzones] = useState([]);

  // Enhanced setRobotStatus wrapper that also handles localStorage
  const setRobotStatusWithPersistence = useCallback(
    (updater) => {
      setRobotStatus((prevStatus) => {
        const newStatus =
          typeof updater === "function" ? updater(prevStatus) : updater;

        // Save currentzone to localStorage when it changes
        if (newStatus.currentzone !== prevStatus.currentzone) {
          saveCurrentZoneToStorage(newStatus.currentzone);
        }

        return newStatus;
      });
    },
    [saveCurrentZoneToStorage],
  );

  // Modal state for loading operations
  const [modalState, setModalState] = useState({
    open: false,
    message: "",
    loading: true,
    currentStatus: null, // For live status updates
  });

  // Ref to store modal timeout ID
  const modalTimeoutRef = useRef(null);

  // State to track operations waiting for specific robot states
  const [pendingOperation, setPendingOperation] = useState({
    isWaiting: false,
    targetState: null,
    operation: null, // 'start', 'pause', 'stop', 'resume'
    initialMessage: "",
  });

  // State to track if drive to dock is in progress
  const [isDriveToDockinProgress, setIsDriveToDockinProgress] = useState(false);

  // State for support modal
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [supportSentForCurrentError, setSupportSentForCurrentError] =
    useState(false);

  // State for manual mode modal (Lionsbot handlebar status)
  const [manualModeModalOpen, setManualModeModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackExperiences, setFeedbackExperiences] = useState([]);
  const [feedbackExperienceComment, setFeedbackExperienceComment] =
    useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedZoneOrder, setSelectedZoneOrder] = useState([]);
  const [multiCleanActive, setMultiCleanActive] = useState(
    String(initialCurrentZone || "")
      .trim()
      .toLowerCase() === NORMALIZED_MULTI_CLEAN_ZONE_NAME,
  );

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // "success", "error", "warning", "info"
  });

  // Cleaning config state
  const [selectedCleaningConfig, setSelectedCleaningConfig] =
    useState("standard");
  const [customCleaningValues, setCustomCleaningValues] = useState({
    brush_height: 1,
    brush_spin: 2,
    cleaning_speed: 1,
    side_brush_height: 1,
    side_brush_spin: 1,
    spray_interval: 2,
    squeegee_height: 1,
    suction: 2,
  });

  const translatedFeedbackExperienceOptions = useMemo(
    () =>
      FEEDBACK_EXPERIENCE_OPTIONS.map((option) => ({
        ...option,
        label: t(`feedbackModal.experienceOptions.${option.labelKey}`),
      })),
    [language],
  );

  const resetFeedbackForm = useCallback(() => {
    setFeedbackRating(0);
    setFeedbackComment("");
    setFeedbackExperiences([]);
    setFeedbackExperienceComment("");
  }, []);

  // Snackbar helper functions
  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  // SSE message handler for device status updates
  const handleSSEMessage = useCallback(
    (message, conflict) => {
      if (message.type !== "robot_status_updated") {
        return;
      }

      const latestLog =
        message.updateData?.latestLog ||
        message.data?.latestLog ||
        message.latestLog;
      const deviceUpdate =
        message.updateData?.device || message.data?.device || message.device;
      const messageDeviceId =
        message.entityId || deviceUpdate?.uk_device_id || latestLog?.device_id;
      const messageDeviceName =
        deviceUpdate?.name ||
        message.updateData?.device_name ||
        message.data?.device_name ||
        message.device_name;

      if (
        String(messageDeviceId) !== String(rcDeviceId) &&
        messageDeviceName !== deviceStatus?.device_name
      ) {
        return;
      }

      console.log(
        `[DeviceControls] Processing status update for device ${rcDeviceId}:`,
        message,
      );

      // Defer state updates to avoid setState during render warning
      setTimeout(() => {
        setDeviceStatus((prevStatus) => ({
          ...prevStatus,
          ...(deviceUpdate
            ? {
                ...deviceUpdate,
                device_name: deviceUpdate.name ?? prevStatus?.device_name,
              }
            : {}),
          ...(latestLog || {}),
          lastUpdated: message.timestamp,
        }));

        if (deviceUpdate) {
          console.log(
            "🔄 [DeviceControls] Device status updated from real-time data",
          );
        }

        if (latestLog) {
          console.log(
            "🔄 [DeviceControls] Device status updated from latest log data",
          );
        }
      }, 0);
    },
    [rcDeviceId, deviceStatus], // Add deviceStatus to dependencies
  );

  // Subscribe to SSE updates only after device data is loaded
  const { isConnected: sseConnected, error: sseError } = useSSESubscription(
    deviceStatus ? `device_controls_${rcDeviceId}` : null, // Conditional subscription
    handleSSEMessage,
    [rcDeviceId, deviceStatus],
  );

  // Modal helper functions (defined early to avoid hoisting issues)
  const showModal = useCallback((message) => {
    setModalState({
      open: true,
      message: message,
      loading: true,
    });
  }, []);

  const showModalWaitingForState = useCallback(
    (message, targetState, operation) => {
      // Clear any existing timeout
      if (modalTimeoutRef.current) {
        clearTimeout(modalTimeoutRef.current);
      }

      setModalState({
        open: true,
        message: message, // Store just the translation key
        loading: true,
        currentStatus: null,
      });
      setPendingOperation({
        isWaiting: true,
        targetState,
        operation,
        initialMessage: message, // Store translation key
      });

      // Set timeout to auto-close modal after 5 seconds to prevent indefinite waiting
      // Status is now visible in the status bar, so modal doesn't need to stay open
      modalTimeoutRef.current = setTimeout(() => {
        console.log(
          `⏰ [ModalHandler] Timeout reached, auto-closing modal for operation: ${operation}`,
        );
        hideModal();
      }, 5000);

      console.log(
        `⏳ [ModalHandler] Started waiting for state: ${targetState} (5s timeout)`,
      );
    },
    [],
  );

  const hideModal = useCallback(() => {
    // Clear any existing timeout
    if (modalTimeoutRef.current) {
      clearTimeout(modalTimeoutRef.current);
      modalTimeoutRef.current = null;
    }

    setModalState({
      open: false,
      message: "",
      loading: true,
      currentStatus: null,
    });
    // Clear any pending operation when manually closing modal
    setPendingOperation({
      isWaiting: false,
      targetState: null,
      operation: null,
      initialMessage: "",
    });
  }, []);

  // Wrapper function to fetch all device information
  const fetchAllDeviceInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDeviceError(null);

    try {
      // Execute both fetch operations in parallel
      const [deviceResult, zoneResult, cleaningConfigResult] =
        await Promise.allSettled([
          getDeviceData(rcDeviceId),
          getDeviceZoneGroups(rcDeviceId),
          getDeviceCleaningConfigs(rcDeviceId),
        ]);

      // Handle device data result
      if (deviceResult.status === "fulfilled") {
        setDeviceStatus(deviceResult.value);
        console.log("Device Status data loaded:", deviceResult.value);
      } else {
        console.error("Error loading devices:", deviceResult.reason.message);
        setError("Failed to load devices");
      }

      // Handle zone data result
      if (zoneResult.status === "fulfilled") {
        setDeviceZoneData(zoneResult.value);
        console.log("Device Zone data loaded:", zoneResult.value);
      } else {
        console.error(
          "Error loading device zone data:",
          zoneResult.reason.message,
        );
        setDeviceError("Failed to load device zone data");
      }

      // Handle cleaning config data result
      if (cleaningConfigResult.status === "fulfilled") {
        const hasCleaningConfigs =
          Array.isArray(cleaningConfigResult.value) &&
          cleaningConfigResult.value.length > 0;

        setCleaningConfigPresent(hasCleaningConfigs);
        console.log("Cleaning Config data loaded:", cleaningConfigResult.value);
      } else {
        console.error(
          "Error loading cleaning config data:",
          cleaningConfigResult.reason.message,
        );
        setCleaningConfigPresent(false);
        setError("Failed to load cleaning config data");
      }
    } catch (err) {
      console.error("Unexpected error during data fetch:", err);
      setError("Failed to load device information");
    } finally {
      setLoading(false);
    }
  }, [rcDeviceId]);

  const handleRefreshWithFullscreen = useCallback(async () => {
    // Fullscreen does not persist across hard reloads; refresh data in-place instead.
    await enterFullscreen();
    await fetchAllDeviceInfo();
  }, [enterFullscreen, fetchAllDeviceInfo]);

  // Only run once on mount
  useEffect(() => {
    fetchAllDeviceInfo();
  }, [fetchAllDeviceInfo]);

  // Process deviceZoneData into zones with state management
  useEffect(() => {
    if (deviceZoneData && Array.isArray(deviceZoneData)) {
      const activeZoneGroups = deviceZoneData.filter(
        (zoneGroup) => Number(zoneGroup.active) === 1,
      );

      const processedzones = activeZoneGroups.map((zoneGroup, index) => ({
        // Use index as id for React key and button handling
        id: zoneGroup.zone_group_pk_id,
        name: zoneGroup.zone_group_display_name_dk,
        nameEn: zoneGroup.zone_group_display_name,
        status: "idle",
        // Preserve all original zone group data
        ...zoneGroup,
      }));
      setzones(processedzones);
      console.log("Processed zones from deviceZoneData:", processedzones);
    }
  }, [deviceZoneData]);

  // Map device operating states to robot UI states
  const mapDeviceStateToRobotState = useCallback(
    (operatingState, batteryLevel) => {
      const normalizedState = String(operatingState || "")
        .trim()
        .toLowerCase();

      switch (normalizedState) {
        case "charging":
        case "charged":
        case "docked":
          return batteryLevel === 100 ? "charged" : "charging";

        case "cleaning":
          return "cleaning";

        case "resting":
        case "idle":
        case "stationary":
          return "idle";

        case "error":
        case "offline":
        case "fault":
          return "error";

        case "returning":
        case "docking in progress":
        case "docking move":
          return "returning";

        case "stopped":
        case "paused":
        case "docking paused":
        case "cleaning paused":
          return "stopped";

        case "teleoping":
        case "teleop clean":
          return "unavailable";

        case "undocking":
          return "preparing";

        default:
          return normalizedState ? "unavailable" : "idle";
      }
    },
    [],
  );

  // Sync robotStatus with live deviceStatus while preserving user-selected zones
  useEffect(() => {
    if (deviceStatus && deviceStatus.operating_state) {
      const liveState = mapDeviceStateToRobotState(
        deviceStatus.operating_state,
        deviceStatus.battery_level || 0,
      );

      setRobotStatusWithPersistence((prevStatus) => {
        // Only update if the state actually changed to avoid unnecessary re-renders
        if (
          prevStatus.state !== liveState ||
          prevStatus.batteryLevel !== deviceStatus.battery_level
        ) {
          console.log(
            `🔄 [StateSync] Updating robot state: ${prevStatus.state} → ${liveState}` +
              (prevStatus.state !== liveState
                ? " (real state changed)"
                : " (battery updated)") +
              `, preserving currentzone: "${prevStatus.currentzone}"`,
          );

          // Determine if we should clear the currentzone based on state transitions
          let newCurrentZone = prevStatus.currentzone;

          // Clear zone when robot finishes cleaning and returns to dock/idle
          if (
            (prevStatus.state === "cleaning" ||
              prevStatus.state === "stopped") &&
            (liveState === "charging" || liveState === "idle")
          ) {
            newCurrentZone = "";
            console.log(
              `🧹 [StateSync] Clearing currentzone - robot returned to ${liveState}`,
            );
            if (liveState === "charging") {
              showSnackbar(
                "Robot has returned to dock and is charging",
                "success",
              );
            }
          }

          // Clear zone on error states
          if (liveState === "error") {
            newCurrentZone = "";
            console.log(
              `❌ [StateSync] Clearing currentzone - robot in error state`,
            );
            showSnackbar("Robot has encountered an error", "error");
          }

          // Show notifications for significant state changes
          if (prevStatus.state !== liveState) {
            switch (liveState) {
              case "cleaning":
                if (prevStatus.state !== "cleaning") {
                  showSnackbar("Robot started cleaning", "success");
                }
                break;
              case "stopped":
                if (prevStatus.state === "cleaning") {
                  showSnackbar("Robot has stopped cleaning", "info");
                }
                break;
              case "returning":
                showSnackbar("Robot is returning to dock", "info");
                break;
            }
          }

          return {
            ...prevStatus,
            state: liveState,
            batteryLevel: deviceStatus.battery_level || prevStatus.batteryLevel,
            chargingProgress:
              deviceStatus.battery_level || prevStatus.chargingProgress,
            currentzone: newCurrentZone, // Preserve or clear based on state logic
          };
        }
        return prevStatus;
      });
    }
  }, [
    deviceStatus?.operating_state,
    deviceStatus?.battery_level,
    mapDeviceStateToRobotState,
    setRobotStatusWithPersistence,
    showSnackbar,
  ]);

  // Check for Lionsbot manual mode (handlebar status)
  useEffect(() => {
    if (
      deviceStatus &&
      deviceStatus.manufacturer === "lionsbot" &&
      deviceStatus.handlebar_status === 1
    ) {
      setManualModeModalOpen(true);
    } else {
      setManualModeModalOpen(false);
    }
  }, [deviceStatus?.handlebar_status, deviceStatus?.manufacturer]);

  // Handle modal updates based on robot status changes when operations are pending
  useEffect(() => {
    if (
      pendingOperation.isWaiting &&
      modalState.open &&
      deviceStatus?.operating_state
    ) {
      // Use raw operating state for more granular status display
      const rawState = deviceStatus?.operating_state;

      // Map raw states to user-friendly display text
      const statusDisplayMap = {
        charging: t("modalStatus.charging") || "Charging...",
        charged: t("modalStatus.charged") || "Charged",
        docked: t("modalStatus.docked") || "Docked",
        undocking: t("modalStatus.undocking") || "Undocking...",
        cleaning: t("modalStatus.cleaning") || "Cleaning...",
        resting: t("modalStatus.resting") || "Resting",
        idle: t("modalStatus.idle") || "Idle",
        stationary: t("modalStatus.stationary") || "Stationary",
        error: t("modalStatus.error") || "Error",
        fault: t("modalStatus.fault") || "Fault",
        returning: t("modalStatus.returning") || "Returning...",
        "docking in progress":
          t("modalStatus.dockingInProgress") || "Docking...",
        "docking move": t("modalStatus.dockingMove") || "Moving to dock...",
        stopped: t("modalStatus.stopped") || "Stopped",
        paused: t("modalStatus.paused") || "Paused",
        "docking paused": t("modalStatus.dockingPaused") || "Docking paused",
        "cleaning paused": t("modalStatus.cleaningPaused") || "Cleaning paused",
        teleoping: t("modalStatus.teleoping") || "Remote control",
        "teleop clean": t("modalStatus.teleopClean") || "Remote cleaning",
      };

      const currentStatusText = statusDisplayMap[rawState] || rawState;

      // Check for error states first - show error for 2 seconds then close
      if (rawState === "error" || rawState === "fault") {
        console.log(
          `❌ [ModalHandler] Error state '${rawState}' detected, showing for 2 seconds then closing`,
        );

        // Clear the timeout since we're handling error state
        if (modalTimeoutRef.current) {
          clearTimeout(modalTimeoutRef.current);
          modalTimeoutRef.current = null;
        }

        setModalState((prev) => ({
          ...prev,
          currentStatus: currentStatusText,
          loading: false, // Stop loading spinner for error state
        }));

        // Close modal after 2 seconds for error states
        setTimeout(() => {
          hideModal();
        }, 2000);

        return; // Exit early for error states
      }

      // Check if we've reached the target state (using raw operating state)
      if (rawState === pendingOperation.targetState) {
        console.log(
          `✅ [ModalHandler] Target state '${pendingOperation.targetState}' reached, closing modal`,
        );

        // Clear the timeout since target state was reached
        if (modalTimeoutRef.current) {
          clearTimeout(modalTimeoutRef.current);
          modalTimeoutRef.current = null;
        }

        setModalState((prev) => ({
          ...prev,
          currentStatus: currentStatusText,
          loading: false,
        }));
        setTimeout(() => {
          hideModal();
        }, 2000);
      } else {
        // Update modal with current status while waiting (only if status changed)
        if (modalState.currentStatus !== currentStatusText) {
          console.log(
            `⏳ [ModalHandler] Waiting for '${pendingOperation.targetState}', current: '${rawState}'`,
          );
          setModalState((prev) => ({
            ...prev,
            currentStatus: currentStatusText,
            loading: true,
          }));
        }
      }
    }
  }, [
    deviceStatus?.operating_state,
    pendingOperation.isWaiting,
    pendingOperation.targetState,
    modalState.open,
    modalState.currentStatus,
    hideModal,
  ]);

  // Sync zone button states with robotStatus changes
  useEffect(() => {
    if (zones.length > 0) {
      setzones((prevZones) => {
        const nextZones = prevZones.map((zone) => {
          const isMultiCleanCurrentZone =
            multiCleanActive &&
            String(robotStatus.currentzone || "")
              .trim()
              .toLowerCase() === NORMALIZED_MULTI_CLEAN_ZONE_NAME;

          switch (robotStatus.state) {
            case "cleaning":
              if (isMultiCleanCurrentZone) {
                return { ...zone, status: "disabled" };
              }

              // If robot is cleaning and this is the current zone, set to stop
              // Otherwise disable other zones
              if (zone.name === robotStatus.currentzone) {
                return { ...zone, status: "cleaning" };
              } else {
                return { ...zone, status: "disabled" };
              }

            case "stopped":
            case "cleaning paused":
            case "docking paused":
            case "p2p paused":
              if (isMultiCleanCurrentZone) {
                return { ...zone, status: "disabled" };
              }

              // If robot is stopped/paused and this is the current zone, set to paused
              // Reset others to start
              if (zone.name === robotStatus.currentzone) {
                return { ...zone, status: "paused" };
              } else {
                return { ...zone, status: "start" };
              }

            case "idle":
            case "charging":
            case "charged":
              // Robot is idle or charging - all zones available to start
              return { ...zone, status: "start" };

            case "returning":
              // Robot returning to dock - disable all zones temporarily
              return { ...zone, status: "disabled" };

            case "error":
              // Robot in error - keep current state but buttons will be disabled by getButtonStatus
              return zone;

            default:
              return zone;
          }
        });

        const hasStatusChanges = nextZones.some(
          (zone, index) => zone.status !== prevZones[index].status,
        );

        return hasStatusChanges ? nextZones : prevZones;
      });

      console.log(
        `🔘 [ButtonSync] Updated zone states for robot state: ${robotStatus.state}`,
        "Current zone: ",
        robotStatus.currentzone,
      );
    }
  }, [multiCleanActive, robotStatus.state, robotStatus.currentzone, zones]);

  useEffect(() => {
    const normalizedCurrentZone = String(robotStatus.currentzone || "")
      .trim()
      .toLowerCase();
    const isPersistedMultiCleanZone =
      normalizedCurrentZone === NORMALIZED_MULTI_CLEAN_ZONE_NAME;
    const isActiveCleaningState = [
      "cleaning",
      "stopped",
      "returning",
      "preparing",
    ].includes(robotStatus.state);

    const shouldBeMultiCleanActive =
      isPersistedMultiCleanZone && isActiveCleaningState;

    if (multiCleanActive !== shouldBeMultiCleanActive) {
      setMultiCleanActive(shouldBeMultiCleanActive);
    }
  }, [multiCleanActive, robotStatus.currentzone, robotStatus.state]);

  useEffect(() => {
    if (!multiCleanActive) {
      return;
    }

    const multiCleanFinished = ["idle", "charging", "charged"].includes(
      robotStatus.state,
    );

    if (multiCleanFinished) {
      setMultiCleanActive(false);
      setSelectedZoneOrder([]);
      setSelectionMode(false);
    }
  }, [multiCleanActive, robotStatus.state]);

  const previousRobotStateRef = useRef(robotStatus.state);
  useEffect(() => {
    const previousState = previousRobotStateRef.current;
    const currentState = robotStatus.state;

    const wasCleaningInProgress = [
      "cleaning",
      "stopped",
      "returning",
      "preparing",
    ].includes(previousState);
    const isCleaningCompleted = ["idle", "charging", "charged"].includes(
      currentState,
    );

    if (
      wasCleaningInProgress &&
      isCleaningCompleted &&
      cleaningLevel !== DEFAULT_CLEANING_LEVEL
    ) {
      setCleaningLevel(DEFAULT_CLEANING_LEVEL);
    }

    previousRobotStateRef.current = currentState;
  }, [cleaningLevel, robotStatus.state]);

  // Simulate backend call
  const simulateBackendCall = (duration = 2000) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true });
      }, duration);
    });
  };

  // Unified device action handler
  const handleDeviceAction = async (
    zoneId,
    zoneName,
    action,
    cleaningLevelValue = cleaningLevel,
    zoneIds = null,
  ) => {
    // Check for Lionsbot manual mode first
    console.log("Zone name on handle device action:", zoneName);
    if (
      deviceStatus?.manufacturer === "lionsbot" &&
      deviceStatus?.handlebar_status === 1
    ) {
      console.log("Device is in manual mode, ignoring action request");
      return false;
    }

    // Don't allow action if robot is in error state (except for dock which might be needed for recovery)
    if (robotStatus.state === "error" && action !== "dock") {
      showModal("actionNotPossible");
      setTimeout(() => hideModal(), 3000);
      return false;
    }

    const buildCleaningConfigPayload = () => {
      let cleaningConfig = null;

      if (SHOW_CLEANING_CONFIG_SELECTOR) {
        if (selectedCleaningConfig === "custom") {
          cleaningConfig = {
            type: "custom",
            values: Object.entries(customCleaningValues).map(
              ([name, value]) => ({
                name,
                value,
              }),
            ),
          };
        } else if (selectedCleaningConfig !== "standard") {
          cleaningConfig = {
            type: "preset",
            preset: selectedCleaningConfig,
          };
        }

        if (cleaningLevelValue != null) {
          if (!cleaningConfig) cleaningConfig = {};
          cleaningConfig.cleaningLevel = cleaningLevelValue;
        }
      }

      return SHOW_CLEANING_CONFIG_SELECTOR
        ? cleaningConfig
        : cleaningLevelValue;
    };

    // Define action configuration
    const actionConfig = {
      start: {
        modalMessage: "gettingReady",
        targetState: "cleaning",
        apiCall: () =>
          sendDeviceControlCommand(
            rcDeviceId,
            "start",
            zoneId,
            buildCleaningConfigPayload(),
          ),
        setRobotStatus: (prevStatus) => ({
          ...prevStatus,
          currentzone: zoneName,
        }),
        updateZoneStatus: "cleaning",
        updateRobotZone: true,
      },
      multiStart: {
        modalMessage: "gettingReady",
        targetState: "cleaning",
        apiCall: () =>
          sendMultiZoneControlCommand(
            rcDeviceId,
            "start",
            zoneIds,
            buildCleaningConfigPayload(),
          ),
        updateRobotZone: true,
      },
      pause: {
        modalMessage: "stopping",
        targetState: "cleaning paused",
        apiCall: () => sendDeviceControlCommand(rcDeviceId, "pause"),
        updateZoneStatus: "paused",
        updateRobotZone: false,
      },
      stop: {
        modalMessage: "stopping",
        targetState: "resting",
        apiCall: () => sendDeviceControlCommand(rcDeviceId, "stop"),
        updateZoneStatus: "idle",
        updateRobotZone: false,
      },
      resume: {
        modalMessage: "resuming",
        targetState: "cleaning",
        apiCall: () => sendDeviceControlCommand(rcDeviceId, "resume"),
        updateZoneStatus: "cleaning",
        updateRobotZone: false,
      },
      dock: {
        modalMessage: "returningToDock",
        targetState: "docked",
        apiCall: () => sendDeviceControlCommand(rcDeviceId, "dock"),
        updateZoneStatus: "start", // Reset all zones to start
        updateRobotZone: false,
        isGlobalAction: true, // Affects all zones
      },
    };

    const config = actionConfig[action];
    if (!config) {
      console.error(`Unknown action: ${action}`);
      return false;
    }

    // Set progress state for dock action
    if (action === "dock") {
      setIsDriveToDockinProgress(true);
    }

    // Update robot status immediately for instant feedback
    if (config.updateRobotZone && zoneName) {
      console.log(
        `🎯 [DeviceAction] Setting currentzone to: "${zoneName}" for action: ${action}`,
      );
      setRobotStatusWithPersistence((prevStatus) => ({
        ...prevStatus,
        currentzone: zoneName,
        state: "cleaning", // Immediately show cleaning state
      }));
    }

    // Show modal and start operation
    showModalWaitingForState(config.modalMessage, config.targetState, action);

    try {
      const result = await config.apiCall();

      // For start action, check API response
      if (action === "start" || action === "multiStart") {
        console.log("DEBUG Action is Start with result:", result);
        const commandSucceeded =
          result?.success === true ||
          result?.connectorResponse?.result?.success === true;

        if (!commandSucceeded || result?.error) {
          // API call failed - revert robot status and show error
          if (config.updateRobotZone && zoneName) {
            console.log(
              `❌ [DeviceAction] API call failed - clearing currentzone for: ${action}`,
            );
            setRobotStatusWithPersistence((prevStatus) => ({
              ...prevStatus,
              currentzone: "",
              state: "idle", // Revert to idle state
            }));
          }
          setzones((prevzones) =>
            prevzones.map((zone) =>
              zone.id === zoneId ? { ...zone, status: "start" } : zone,
            ),
          );
          hideModal();
          showModal("actionNotPossible");
          showSnackbar(
            `Failed to start cleaning ${zoneName}. Please try again.`,
            "error",
          );
          setTimeout(() => hideModal(), 3000);
          return false;
        }
      }

      console.log(`${action} action completed for: ${zoneName || "device"}`);

      // Show success snackbar for completed actions
      switch (action) {
        case "start":
          showSnackbar(`Started cleaning ${zoneName}`, "success");
          break;
        case "multiStart":
          showSnackbar(
            `Started multi-clean for ${zoneIds?.length || 0} zones`,
            "success",
          );
          break;
        case "pause":
          showSnackbar("Robot paused", "info");
          break;
        case "stop":
          showSnackbar("Robot stopped", "info");
          break;
        case "resume":
          showSnackbar("Robot resumed cleaning", "success");
          break;
        case "dock":
          showSnackbar("Robot returning to dock", "info");
          break;
      }
      // Robot status already updated above for immediate feedback

      // Update zone states
      if (config.isGlobalAction) {
        // Update all zones (for dock action)
        setzones((prevzones) =>
          prevzones.map((zone) => ({
            ...zone,
            status: config.updateZoneStatus,
          })),
        );
      } else if (zoneId && config.updateZoneStatus) {
        // Update specific zone
        setzones((prev) =>
          prev.map((zone) =>
            zone.id === zoneId
              ? { ...zone, status: config.updateZoneStatus }
              : zone,
          ),
        );
      }

      return true;
    } catch (error) {
      console.error(`Failed to ${action}:`, error);

      // Revert robot status on error
      if (config.updateRobotZone && zoneName) {
        console.log(
          `❌ [DeviceAction] Clearing currentzone due to action error for: ${action}`,
        );
        setRobotStatusWithPersistence((prevStatus) => ({
          ...prevStatus,
          currentzone: "",
          state: "idle", // Revert to idle state
        }));
      }

      // Reset zone status on error (except for dock)
      if (!config.isGlobalAction && zoneId) {
        setzones((prevzones) =>
          prevzones.map((zone) =>
            zone.id === zoneId ? { ...zone, status: "start" } : zone,
          ),
        );
      }

      hideModal();
      showModal("actionNotPossible");
      showSnackbar(`Failed to ${action} robot. Please try again.`, "error");
      setTimeout(() => hideModal(), 3000);
      return false;
    } finally {
      // Clean up dock progress state
      if (action === "dock") {
        setIsDriveToDockinProgress(false);
      }
    }
  };

  const handleDriveToDock = () => handleDeviceAction(null, null, "dock");

  const handleCancelDocking = () => {
    // Stop the docking process
    const command = "stop";
    handleDeviceAction(null, null, command);
    setIsDriveToDockinProgress(false);
  };

  const handleContactSupport = () => {
    setSupportModalOpen(true);
  };

  const handleCloseSupportModal = () => {
    setSupportModalOpen(false);
    setSupportMessage("");
  };

  const handleSendSupport = async () => {
    showModal("sendingSupportRequest");
    setSupportModalOpen(false);

    try {
      const result = await sendNotification({
        message: "Robot Error - Support Requested",
        customerName: customerName || "Unknown Customer",
        location: deviceStatus?.location_name || "Unknown Location",
        robotId: rcDeviceId || "Unknown ID",
        robotName: deviceStatus?.device_name || "Unknown Device",
        error: `Robot in ${robotStatus.state} state.`,
        responseCodes: deviceStatus?.response_codes || "Unknown Response Codes",
        customerMessage: supportMessage,
      });
      if (result.success) {
        setSupportSentForCurrentError(true);
        showSnackbar(t("supportModal.success"), "success");
      } else {
        showSnackbar(t("supportModal.error"), "error");
      }
    } catch (error) {
      console.error("Failed to send support request:", error);
      showSnackbar(t("supportModal.error"), "error");
    } finally {
      setSupportMessage("");
      hideModal();
    }
  };

  const handleSettings = () => {
    // Placeholder for settings action
    showSnackbar("Settings clicked", "info");
  };

  const isWithinFeedbackWindow = useCallback(() => {
    const now = new Date();
    const hour = now.getHours();
    return hour >= FEEDBACK_STARTING_HOUR && hour < FEEDBACK_EXPIRATION_HOUR;
  }, []);

  const getFeedbackStorageKey = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    const userKey = user?.username || "anonymous";
    const locationKey = String(deviceStatus?.location_pk_id || rcDeviceId);
    return `customer_feedback_submitted_${userKey}_${locationKey}_${today}`;
  }, [deviceStatus?.location_pk_id, rcDeviceId, user?.username]);

  useEffect(() => {
    if (!user || !deviceStatus) {
      return;
    }

    if (!isWithinFeedbackWindow()) {
      setFeedbackModalOpen(false);
      resetFeedbackForm();
      return;
    }

    const storageKey = getFeedbackStorageKey();
    const alreadySubmitted = localStorage.getItem(storageKey) === "1";
    if (!alreadySubmitted) {
      setFeedbackModalOpen(true);
    }
  }, [
    deviceStatus,
    getFeedbackStorageKey,
    isWithinFeedbackWindow,
    resetFeedbackForm,
    user,
  ]);

  const handleCloseFeedbackModal = useCallback(() => {
    setFeedbackModalOpen(false);
    resetFeedbackForm();
  }, [resetFeedbackForm]);

  const toggleFeedbackExperience = useCallback((experienceValue) => {
    setFeedbackExperiences((previousValues) => {
      if (previousValues.includes(experienceValue)) {
        return previousValues.filter((value) => value !== experienceValue);
      }

      return [...previousValues, experienceValue];
    });
  }, []);

  useEffect(() => {
    if (feedbackRating > LOW_RATING_THRESHOLD) {
      if (feedbackExperiences.length) {
        setFeedbackExperiences([]);
      }
      if (feedbackExperienceComment) {
        setFeedbackExperienceComment("");
      }
    }
  }, [feedbackExperienceComment, feedbackExperiences.length, feedbackRating]);

  const handleSubmitFeedback = useCallback(
    async (selectedRating) => {
      const ratingToSubmit =
        typeof selectedRating === "number" ? selectedRating : feedbackRating;

      if (!ratingToSubmit || ratingToSubmit < 1 || ratingToSubmit > 5) {
        showSnackbar("Please choose a rating between 1 and 5", "warning");
        return;
      }

      const requiresExperienceSelection =
        ratingToSubmit <= LOW_RATING_THRESHOLD;

      if (requiresExperienceSelection && feedbackExperiences.length === 0) {
        showSnackbar(
          "Please select at least one floor experience before submitting",
          "warning",
        );
        return;
      }

      setFeedbackSubmitting(true);
      try {
        const experienceCommentTrimmed = feedbackExperienceComment.trim();
        const commentLine = experienceCommentTrimmed
          ? `Comment: ${experienceCommentTrimmed}`
          : "";
        const feedbackCommentPayload = requiresExperienceSelection
          ? [...feedbackExperiences, commentLine].filter(Boolean).join("\n")
          : feedbackComment.trim();

        await submitCustomerFeedback({
          deviceId: rcDeviceId,
          rating: ratingToSubmit,
          comment: feedbackCommentPayload,
        });

        localStorage.setItem(getFeedbackStorageKey(), "1");
        setFeedbackModalOpen(false);
        resetFeedbackForm();
        showSnackbar("Thank you for your feedback", "success");
      } catch (submitError) {
        showSnackbar(
          `Failed to submit feedback: ${submitError.message}`,
          "error",
        );
      } finally {
        setFeedbackSubmitting(false);
      }
    },
    [
      feedbackComment,
      feedbackExperienceComment,
      feedbackExperiences,
      feedbackRating,
      getFeedbackStorageKey,
      rcDeviceId,
      resetFeedbackForm,
      showSnackbar,
    ],
  );

  //

  const handleButtonClick = async (zoneId, zoneName, currentStatus) => {
    console.log(
      `handleButtonClick called with: zoneId=${zoneId}, zoneName=${zoneName}, currentStatus=${currentStatus}`,
    );

    if (zoneId === MULTI_CLEAN_ZONE_ID) {
      if (currentStatus === "start") {
        if (selectedZoneOrder.length < 2) {
          showSnackbar(
            "Select at least 2 zones to start multi-clean",
            "warning",
          );
          return;
        }

        const started = await handleDeviceAction(
          zoneId,
          MULTI_CLEAN_ZONE_NAME,
          "multiStart",
          cleaningLevel,
          selectedZoneOrder,
        );

        if (started) {
          setSelectionMode(false);
          setMultiCleanActive(true);
          setRobotStatusWithPersistence((prevStatus) => ({
            ...prevStatus,
            currentzone: MULTI_CLEAN_ZONE_NAME,
            state: "cleaning",
          }));
        }

        return;
      }

      if (currentStatus === "cleaning" || currentStatus === "stop") {
        if (robotStatus.state === "cleaning") {
          await handleDeviceAction(
            null,
            MULTI_CLEAN_ZONE_NAME,
            "pause",
            cleaningLevel,
          );
        } else {
          const stopped = await handleDeviceAction(
            null,
            MULTI_CLEAN_ZONE_NAME,
            "stop",
            cleaningLevel,
          );

          if (stopped) {
            setMultiCleanActive(false);
            setSelectedZoneOrder([]);
            setSelectionMode(false);
          }
        }
        return;
      }

      if (currentStatus === "resume") {
        await handleDeviceAction(
          null,
          MULTI_CLEAN_ZONE_NAME,
          "resume",
          cleaningLevel,
        );
        return;
      }
    }

    // Check for Lionsbot manual mode first
    if (
      deviceStatus?.manufacturer === "lionsbot" &&
      deviceStatus?.handlebar_status === 1
    ) {
      console.log("Device is in manual mode, ignoring button click");
      return;
    }

    // Don't allow action if robot is in error state
    if (robotStatus.state === "error") {
      console.log("Robot is in error state, ignoring button click");
      return;
    }
    // Call appropriate handler based on current status
    switch (currentStatus) {
      case "start":
        console.log("Handling start action");
        // idle -> cleaning (button will show "stop")
        handleDeviceAction(zoneId, zoneName, "start", cleaningLevel);
        // Update zone status to cleaning
        setzones((prev) =>
          prev.map((zone) =>
            zone.id === zoneId ? { ...zone, status: "cleaning" } : zone,
          ),
        );
        break;
      case "cleaning":
      case "stop":
        // cleaning -> paused (buttons will show "stop" and "resume")
        // OR paused -> idle (buttons will show "start")
        const zone = zones.find((a) => a.id === zoneId);
        console.log("Handling stop action. Current zone status:", zone.status);
        if (zone?.status === "cleaning") {
          console.log("zone is cleaning, pausing...");
          // First stop - pause the robot
          handleDeviceAction(zoneId, zoneName, "pause", cleaningLevel);
          setzones((prev) =>
            prev.map((a) => (a.id === zoneId ? { ...a, status: "paused" } : a)),
          );
        } else if (zone?.status === "paused") {
          console.log("zone is paused, stopping completely...");
          // Second stop - return to idle
          handleDeviceAction(zoneId, zoneName, "stop", cleaningLevel);
          setzones((prev) =>
            prev.map((a) => (a.id === zoneId ? { ...a, status: "idle" } : a)),
          );
        }
        break;
      case "resume":
        console.log("Handling resume action");
        // paused -> cleaning (button will show "stop")
        handleDeviceAction(zoneId, zoneName, "resume", cleaningLevel);
        setzones((prev) =>
          prev.map((zone) =>
            zone.id === zoneId ? { ...zone, status: "cleaning" } : zone,
          ),
        );
        break;
      default:
        console.log(`Unknown status: ${currentStatus}`);
    }
  };

  // Function to get button status considering robot state
  const getButtonStatus = (zone) => {
    if (
      robotStatus.state === "error" ||
      (deviceStatus?.manufacturer === "lionsbot" &&
        deviceStatus?.handlebar_status === 1)
    ) {
      return "disabled";
    }
    switch (zone.status) {
      case "idle":
        return "start";
      case "cleaning":
        return "stop";
      case "paused":
        return "paused"; // Special state for dual buttons
      default:
        return zone.status;
    }
  };

  // Function to determine if button should be disabled
  const isButtonDisabled = (zone) => {
    return (
      robotStatus.state === "error" ||
      (deviceStatus?.manufacturer === "lionsbot" &&
        deviceStatus?.handlebar_status === 1) ||
      zone.status === "disabled" ||
      zone.status === "unavailable"
    );
  };

  const getStatusText = (state, currentzone) => {
    switch (state) {
      case "charging":
        return t("status.charging");
      case "charged":
        return t("status.charged");
      case "cleaning":
        return `${t("status.cleaningzone")} ${currentzone}`;
      case "stopped":
        return t("status.stopped");
      case "error":
        return t("status.error");
      case "returning":
        return t("status.returning");
      case "Unavailable":
        return t("status.unavailable");
      default:
        return state;
    }
  };

  const enableSelectionMode = () => {
    setSelectionMode(true);
  };

  const cancelSelectionMode = () => {
    setSelectionMode(false);
    setSelectedZoneOrder([]);
  };

  const handleToggleZoneSelection = (zoneId) => {
    setSelectedZoneOrder((prev) => {
      if (prev.includes(zoneId)) {
        return prev.filter((id) => id !== zoneId);
      }
      return [...prev, zoneId];
    });
  };

  const normalizedOperatingState = String(deviceStatus?.operating_state || "")
    .trim()
    .toLowerCase();
  const isSingleZonePaused = [
    "paused",
    "cleaning paused",
    "docking paused",
    "p2p paused",
  ].includes(normalizedOperatingState);
  const isRobotBusyWithoutMultiClean =
    robotStatus.state === "cleaning" ||
    robotStatus.state === "returning" ||
    robotStatus.state === "preparing" ||
    (robotStatus.state === "stopped" && !isSingleZonePaused);

  const selectorZone = {
    id: MULTI_CLEAN_ZONE_ID,
    name: MULTI_CLEAN_ZONE_NAME,
    nameEn: MULTI_CLEAN_ZONE_NAME,
    status: multiCleanActive
      ? robotStatus.state === "cleaning"
        ? "cleaning"
        : [
              "stopped",
              "cleaning paused",
              "docking paused",
              "p2p paused",
            ].includes(robotStatus.state)
          ? "paused"
          : "idle"
      : isRobotBusyWithoutMultiClean
        ? "disabled"
        : "idle",
  };

  const guideManufacturer = String(deviceStatus?.manufacturer || "")
    .trim()
    .toLowerCase();
  const guidePath = `/guides?manufacturer=${encodeURIComponent(guideManufacturer || "general")}`;

  const sidebarProps = {
    user,
    language,
    toggleLanguage,
    zones,
    robotStatus,
    isDriveToDockinProgress,
    handleDriveToDock,
    handleCancelDocking,
    handleContactSupport,
    handleSettings,
    supportSentForCurrentError,
    deviceStatus,
    sseConnected,
    t,
    cleaningLevel,
    setCleaningLevel,
    cleaningConfigPresent,
    guidePath,
  };

  useEffect(() => {
    if (!isMobile && mobileSidebarOpen) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile, mobileSidebarOpen]);

  return (
    <>
      <TopNavBar title={"title"} username={username} heading={t("title")} />

      {isMobile && !mobileSidebarOpen && (
        <Box
          sx={{
            position: "fixed",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: (zIndexTheme) => zIndexTheme.zIndex.drawer + 2,
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
            overflow: "hidden",
            boxShadow: 3,
          }}
        >
          <Button
            onClick={() => setMobileSidebarOpen(true)}
            variant="contained"
            aria-label="Open control panel"
            sx={{
              minWidth: 0,
              px: 0.75,
              py: 1,
              borderRadius: 0,
              borderTopLeftRadius: 12,
              borderBottomLeftRadius: 12,
              width: 34,
              minHeight: 52,
            }}
          >
            <TuneIcon sx={{ fontSize: 18 }} />
          </Button>
        </Box>
      )}

      <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
        {/* Main Content zone */}
        <Box
          sx={{
            flexGrow: 1,
            pt: { xs: 1.5, md: 3 },
            pb: { xs: 1.5, md: 3 },
            pl: { xs: 1.5, md: 3 },
            pr: { xs: 0, md: 3 },
          }}
        >
          {/* Status Bar */}
          <Paper
            elevation={2}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: getStatusBarBackgroundColor(robotStatus.state),
              border: `2px solid ${getStatusBarBorderColor(robotStatus.state)}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {getStatusIcon(robotStatus.state)}
              <Typography
                variant="h6"
                sx={{ flexGrow: 1, textAlign: "center" }}
              >
                {getStatusText(
                  robotStatus.state,
                  robotStatus.currentzone,
                ).toUpperCase()}
              </Typography>
              {robotStatus.state === "charging" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    minWidth: 200,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={robotStatus.chargingProgress}
                    sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {robotStatus.chargingProgress}%
                  </Typography>
                </Box>
              )}
              <IconButton
                onClick={handleRefreshWithFullscreen}
                disabled={loading}
                size="small"
                sx={{ ml: 2 }}
                title="Refresh device data"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
            {/* Live Device Status */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                border: `1px solid ${getStatusBarBorderColor(robotStatus.state)}`,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                mt: 2,
                p: 1,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h8"
                sx={{ flexGrow: 1, textAlign: "center" }}
              >
                {deviceStatus?.operating_state
                  ? `Status: ${deviceStatus.operating_state}`
                  : null}
              </Typography>
              <Typography
                variant="h8"
                sx={{ flexGrow: 1, textAlign: "center" }}
              >
                {deviceStatus?.response_codes
                  ? (() => {
                      const formatResponseCodes = (codes) => {
                        if (Array.isArray(codes)) {
                          return codes
                            .map((code) => {
                              if (typeof code === "string") {
                                return code;
                              }

                              if (code && typeof code === "object") {
                                return [code.code, code.description]
                                  .filter(Boolean)
                                  .join(": ");
                              }

                              return String(code);
                            })
                            .filter(Boolean)
                            .join(", ");
                        }

                        if (
                          codes &&
                          typeof codes === "object" &&
                          !Array.isArray(codes)
                        ) {
                          return [codes.code, codes.description]
                            .filter(Boolean)
                            .join(": ");
                        }

                        return String(codes);
                      };

                      try {
                        const rawCodes = deviceStatus.response_codes;
                        const parsedCodes =
                          typeof rawCodes === "string"
                            ? JSON.parse(rawCodes)
                            : rawCodes;

                        return "Message: " + formatResponseCodes(parsedCodes);
                      } catch {
                        return (
                          "Message: " +
                          formatResponseCodes(deviceStatus.response_codes)
                        );
                      }
                    })()
                  : null}
              </Typography>
            </Box>
          </Paper>

          {/* zones Grid Header with Config Selector, hidden by default */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
              gap: 3,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              {t("heading")}
            </Typography>
            {SHOW_CLEANING_CONFIG_SELECTOR && (
              <Box sx={{ flex: 1, maxWidth: 500 }}>
                <CleaningConfigSelector
                  selectedConfig={selectedCleaningConfig}
                  onConfigChange={setSelectedCleaningConfig}
                  customValues={customCleaningValues}
                  onCustomValuesChange={setCustomCleaningValues}
                />
              </Box>
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : (
            <>
              <Grid
                container
                spacing={3}
                sx={{ maxWidth: 1200, margin: "0 auto" }}
              >
                {!zones || zones.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t("noZones")}
                  </Alert>
                ) : (
                  zones.map((zone) => (
                    <ControlCard
                      key={zone.id}
                      zone={zone}
                      language={language}
                      robotStatus={robotStatus}
                      t={t}
                      getButtonStatus={getButtonStatus}
                      isButtonDisabled={isButtonDisabled}
                      handleButtonClick={handleButtonClick}
                      getButtonBackgroundColor={getButtonBackgroundColor}
                      buttonStyles={buttonStyles}
                      selectionMode={selectionMode}
                      isSelected={selectedZoneOrder.includes(zone.id)}
                      selectionOrder={selectedZoneOrder.indexOf(zone.id) + 1}
                      onToggleSelect={handleToggleZoneSelection}
                      forceDisabled={multiCleanActive}
                    />
                  ))
                )}
                {!!zones?.length && (
                  <ControlCardSelector
                    zone={selectorZone}
                    t={t}
                    getButtonStatus={getButtonStatus}
                    isButtonDisabled={isButtonDisabled}
                    handleButtonClick={handleButtonClick}
                    getButtonBackgroundColor={getButtonBackgroundColor}
                    buttonStyles={buttonStyles}
                    selectionMode={selectionMode}
                    onEnableSelectionMode={enableSelectionMode}
                    onCancelSelectionMode={cancelSelectionMode}
                    selectedCount={selectedZoneOrder.length}
                  />
                )}
              </Grid>
            </>
          )}
        </Box>

        {/* Right Sidebar */}
        {!isMobile && <ControlSidebar {...sidebarProps} />}
      </Box>

      {isMobile && (
        <Drawer
          anchor="right"
          open={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: { xs: "86vw", sm: 320 },
              maxWidth: 360,
            },
          }}
        >
          <ControlSidebar {...sidebarProps} />
        </Drawer>
      )}

      {/* Loading Modal */}
      <Modal
        open={modalState.open}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        closeAfterTransition
      >
        <Fade in={modalState.open}>
          <Box sx={{ ...modalStyles.container, ...modalStyles.loading }}>
            <CircularProgress size={60} sx={{ mb: 3, color: "primary.main" }} />
            <Typography variant="h6" gutterBottom sx={{ textAlign: "center" }}>
              {modalState.open ? t(`loadingModal.${modalState.message}`) : ""}
            </Typography>
            {modalState.currentStatus && (
              <Typography
                variant="body1"
                sx={{ textAlign: "center", mb: 2, fontStyle: "italic" }}
              >
                Current Status: {modalState.currentStatus}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {modalState.open ? t("loadingModal.pleaseWait") : ""}
            </Typography>
          </Box>
        </Fade>
      </Modal>

      {/* Support Modal */}
      <Modal
        open={supportModalOpen}
        onClose={handleCloseSupportModal}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        closeAfterTransition
      >
        <Fade in={supportModalOpen}>
          <Box sx={{ ...modalStyles.container, ...modalStyles.support }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ textAlign: "center", mb: 3 }}
            >
              {t("supportModal.title")}
            </Typography>

            <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
              {t("supportModal.description")}
            </Typography>

            <TextField
              fullWidth
              label={t("supportModal.supportMessageLabel")}
              placeholder=""
              value={supportMessage}
              onChange={(e) => {
                setSupportMessage(e.target.value);
              }}
              type="text"
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleCloseSupportModal}
                sx={{ minWidth: 100 }}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleSendSupport}
                sx={{ minWidth: 100 }}
              >
                {t("send")}
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Manual Mode Modal */}
      <Modal
        open={manualModeModalOpen}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        closeAfterTransition
      >
        <Fade in={manualModeModalOpen}>
          <Box sx={{ ...modalStyles.container, ...modalStyles.support }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ textAlign: "center", mb: 3, color: "warning.main" }}
            >
              {t("manualModeModal.title")}
            </Typography>

            <Typography
              variant="body1"
              gutterBottom
              sx={{ mb: 3, textAlign: "center" }}
            >
              {t("manualModeModal.description")}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={() => setManualModeModalOpen(false)}
                sx={{ minWidth: 100 }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Customer Feedback Modal - shows between set times */}
      <CustomerFeedbackModal
        open={feedbackModalOpen}
        onClose={handleCloseFeedbackModal}
        onSubmit={handleSubmitFeedback}
        rating={feedbackRating}
        onRatingChange={setFeedbackRating}
        comment={feedbackComment}
        onCommentChange={setFeedbackComment}
        submitting={feedbackSubmitting}
        modalSize={FEEDBACK_MODAL_SIZE}
        showComment={FEEDBACK_SHOW_COMMENT}
        expirationHour={FEEDBACK_EXPIRATION_HOUR}
        title={t("feedbackModal.title")}
        submitLabel={t("feedbackModal.submitLabel")}
        closeLabel={t("feedbackModal.closeLabel")}
        lowRatingExperienceEnabled={true}
        lowRatingThreshold={LOW_RATING_THRESHOLD}
        experienceSelections={feedbackExperiences}
        onToggleExperience={toggleFeedbackExperience}
        experienceOptions={translatedFeedbackExperienceOptions}
        experiencePrompt={t("feedbackModal.experiencePrompt")}
        experienceComment={feedbackExperienceComment}
        onExperienceCommentChange={setFeedbackExperienceComment}
        experienceCommentLabel={t("feedbackModal.experienceCommentLabel")}
        savingLabel={t("feedbackModal.savingLabel")}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={hideSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
